import type { TableRowSelection } from 'antd/es/table/interface';
import { useCallback, useMemo } from 'react';

import type {
  BulkOperationOption,
  BulkOperationsConfig,
  BulkOperationType
} from '../../types';

type Props<T> = {
  rowSelection?: TableRowSelection<T>;
  dataSource?: readonly T[];
  bulkOperationConfig?: BulkOperationsConfig;
  selectedBulkOperationType?: BulkOperationType;
  showBulkOperationsButton: boolean;
};

const useBulkOperations = <T extends object>({
  rowSelection,
  dataSource,
  bulkOperationConfig,
  selectedBulkOperationType,
  showBulkOperationsButton
}: Props<T>) => {
  const isBulkOperationsFlow =
    Boolean(rowSelection) && Boolean(bulkOperationConfig?.options);

  const selectedBulkOperation = useMemo<BulkOperationOption | undefined>(() => {
    if (!selectedBulkOperationType || !bulkOperationConfig?.options) {
      return;
    }

    return bulkOperationConfig.options.find(
      opt => opt.type === selectedBulkOperationType
    );
  }, [bulkOperationConfig, selectedBulkOperationType]);

  const rowSelectionConfig = useMemo<TableRowSelection<T> | undefined>(() => {
    if (!rowSelection || !dataSource?.length) return undefined;
    if (!isBulkOperationsFlow) return rowSelection;
    if (!showBulkOperationsButton) return rowSelection;

    return selectedBulkOperation ? rowSelection : undefined;
  }, [
    rowSelection,
    dataSource,
    isBulkOperationsFlow,
    selectedBulkOperation,
    showBulkOperationsButton
  ]);

  const shouldRenderBulkDropDown =
    isBulkOperationsFlow &&
    bulkOperationConfig?.options &&
    bulkOperationConfig.options.length > 0 &&
    showBulkOperationsButton;

  const handleCancelAndClear = useCallback(() => {
    bulkOperationConfig?.onCancelAndClear();
  }, [bulkOperationConfig]);

  return {
    selectedBulkOperation,
    rowSelectionConfig,
    shouldRenderBulkDropDown,
    handleCancelAndClear
  };
};

export default useBulkOperations;
